import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Alert, App, Button, Empty, Image, Input, Select, Spin, Tag, Typography, Upload, Modal } from 'antd';
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
  PaperClipOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { WAConversation, WAMessage, WASendPayload } from '../../types/whatsapp';
import { WATemplate } from '../../types/whatsapp-broadcast';
import * as whatsappService from '../../services/whatsappService';
import * as whatsappBroadcastService from '../../services/whatsappBroadcastService';
import { isWindowOpen } from './ConversationList';

const { Text, Title } = Typography;

interface MessageThreadProps {
  conversation: WAConversation | null;
  messages: WAMessage[];
  loadingMessages: boolean;
  canSend: boolean;
  canAssign: boolean;
  onSend: (payload: WASendPayload) => Promise<void>;
  onAssignClick: () => void;
  onTemplateSent?: () => void;
}

const statusMeta: Record<string, { icon: React.ReactNode; label: string; color?: string }> = {
  queued: { icon: <ClockCircleOutlined />, label: 'Terkirim...' },
  sent: { icon: <CheckCircleOutlined />, label: 'Terkirim' },
  delivered: { icon: <CheckCircleOutlined />, label: 'Diterima' },
  read: { icon: <CheckCircleFilled />, label: 'Dibaca', color: '#1677ff' },
  failed: { icon: <ExclamationCircleFilled />, label: 'Gagal', color: '#ff4d4f' },
  received: { icon: null, label: '' },
};

const MessageThread: React.FC<MessageThreadProps> = ({
  conversation,
  messages,
  loadingMessages,
  canSend,
  canAssign,
  onSend,
  onAssignClick,
  onTemplateSent,
}) => {
  const { message } = App.useApp();
  const [text, setText] = useState('');
  const [pendingImage, setPendingImage] = useState<{ url: string; uploading: boolean } | null>(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templates, setTemplates] = useState<WATemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WATemplate | null>(null);
  const [templateBodyParams, setTemplateBodyParams] = useState<string[]>([]);
  const [templateHeaderImageUrl, setTemplateHeaderImageUrl] = useState<string | undefined>();
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, conversation?.id]);

  useEffect(() => {
    setText('');
    setPendingImage(null);
  }, [conversation?.id]);

  useEffect(() => {
    if (!templateModalOpen) return;
    let active = true;
    (async () => {
      setLoadingTemplates(true);
      try {
        const r = await whatsappBroadcastService.listTemplates();
        const approved = (r.data || []).filter((t: WATemplate) => t.status === 'APPROVED');
        if (active) setTemplates(approved);
      } catch (error) {
        console.error(error);
        if (active) message.error('Gagal memuat daftar template');
      } finally {
        if (active) setLoadingTemplates(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateModalOpen]);

  useEffect(() => {
    if (!templateModalOpen) {
      setSelectedTemplate(null);
      setTemplateBodyParams([]);
      setTemplateHeaderImageUrl(undefined);
    }
  }, [templateModalOpen]);

  const handleTemplateSelect = (name: string) => {
    const t = templates.find((tpl) => tpl.name === name) || null;
    setSelectedTemplate(t);
    setTemplateBodyParams(t ? Array(t.body_param_count).fill('') : []);
    setTemplateHeaderImageUrl(undefined);
  };

  const handleTemplateHeaderUpload = async (file: RcFile) => {
    setUploadingHeader(true);
    try {
      const url = await whatsappService.uploadImage(file);
      setTemplateHeaderImageUrl(url);
    } catch (error) {
      console.error(error);
      message.error('Gagal mengunggah gambar header');
    } finally {
      setUploadingHeader(false);
    }
    return false;
  };

  const handleSendTemplate = async () => {
    if (!conversation || !selectedTemplate) return;
    setSendingTemplate(true);
    try {
      await whatsappBroadcastService.sendTemplate(conversation.id, {
        template_name: selectedTemplate.name,
        language: selectedTemplate.language,
        body_params: templateBodyParams,
        header_image_url: templateHeaderImageUrl,
      });
      message.success('Template berhasil dikirim');
      setTemplateModalOpen(false);
      onTemplateSent?.();
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal mengirim template');
    } finally {
      setSendingTemplate(false);
    }
  };

  const templateSendDisabled =
    !selectedTemplate ||
    templateBodyParams.some((p) => !p.trim()) ||
    (selectedTemplate.header_type === 'image' && !templateHeaderImageUrl);

  if (!conversation) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty description="Pilih percakapan untuk memulai" />
      </div>
    );
  }

  const windowOpen = isWindowOpen(conversation);
  const composerDisabled = !canSend || !windowOpen;

  const handleBeforeUpload = async (file: RcFile) => {
    setPendingImage({ url: '', uploading: true });
    try {
      const url = await whatsappService.uploadImage(file);
      setPendingImage({ url, uploading: false });
    } catch (error) {
      console.error(error);
      message.error('Gagal mengunggah gambar');
      setPendingImage(null);
    }
    return false;
  };

  const handleSend = async () => {
    if (pendingImage) {
      if (pendingImage.uploading) return;
      setSending(true);
      try {
        await onSend({ type: 'image', media_url: pendingImage.url, text: text.trim() || undefined });
        setText('');
        setPendingImage(null);
      } catch (error) {
        // handled upstream
      } finally {
        setSending(false);
      }
      return;
    }

    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      await onSend({ type: 'text', text: trimmed });
      setText('');
    } catch (error) {
      // handled upstream
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: 12,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar icon={<UserOutlined />} size={40} />
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {conversation.contact_name || conversation.wa_phone}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {conversation.wa_phone}
              {conversation.lead_id && (
                <>
                  {' '}
                  &middot;{' '}
                  <Link to={`/leads/${conversation.lead_id}`}>
                    Lihat Lead{conversation.lead?.full_name ? `: ${conversation.lead.full_name}` : ''}
                  </Link>
                </>
              )}
            </Text>
            {conversation.lead?.lead_follow_up_status?.name && (
              <div style={{ marginTop: 2 }}>
                <Tag color="geekblue" style={{ fontSize: 11 }}>
                  Follow-up: {conversation.lead.lead_follow_up_status.name}
                </Tag>
              </div>
            )}
          </div>
        </div>
        {canAssign && <Button onClick={onAssignClick}>Assign</Button>}
      </div>

      {/* Thread */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 4px' }}>
        <Spin spinning={loadingMessages}>
          {messages.map((m) => {
            const outbound = m.direction === 'outbound';
            const meta = statusMeta[(m.status || '').toLowerCase()];
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: outbound ? 'flex-end' : 'flex-start',
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    maxWidth: '65%',
                    background: outbound ? '#d9f2d1' : '#ffffff',
                    border: outbound ? 'none' : '1px solid #f0f0f0',
                    borderRadius: 10,
                    padding: '8px 10px',
                  }}
                >
                  {m.media_url && (m.type === 'image' || m.type === 'template') && (
                    <Image src={m.media_url} alt="gambar" style={{ maxWidth: 220, borderRadius: 6, marginBottom: 6 }} />
                  )}
                  {m.type === 'template' && (
                    <Tag color="green" style={{ fontSize: 10, marginBottom: 4 }}>Template</Tag>
                  )}
                  {m.text && <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 4,
                      marginTop: 4,
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(m.wa_timestamp || m.created_at).format('HH:mm')}
                    </Text>
                    {outbound && meta?.icon && (
                      <span style={{ color: meta.color || '#8c8c8c', fontSize: 12 }} title={meta.label}>
                        {meta.icon}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </Spin>
      </div>

      {/* Window closed notice */}
      {!windowOpen && (
        <Alert
          type="warning"
          showIcon
          message="Jendela 24 jam tertutup — balas menggunakan template WhatsApp approved"
          action={
            canSend && (
              <Button size="small" onClick={() => setTemplateModalOpen(true)}>
                Kirim Template
              </Button>
            )
          }
          style={{ marginBottom: 8 }}
        />
      )}

      {/* Composer */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={handleBeforeUpload}
          disabled={composerDisabled}
        >
          <Button icon={<PaperClipOutlined />} disabled={composerDisabled} />
        </Upload>
        <div style={{ flex: 1 }}>
          {pendingImage && (
            <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              {pendingImage.uploading ? (
                <Tag icon={<ClockCircleOutlined />}>Mengunggah gambar...</Tag>
              ) : (
                <>
                  <Image src={pendingImage.url} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} />
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => setPendingImage(null)}
                    disabled={composerDisabled}
                  />
                </>
              )}
            </div>
          )}
          <Input.TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={pendingImage ? 'Tambahkan caption (opsional)...' : 'Tulis pesan...'}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={composerDisabled}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
        </div>
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          disabled={composerDisabled || (!pendingImage && !text.trim())}
        >
          Kirim
        </Button>
      </div>

      <Modal
        title="Kirim Template"
        open={templateModalOpen}
        onOk={handleSendTemplate}
        onCancel={() => setTemplateModalOpen(false)}
        confirmLoading={sendingTemplate}
        destroyOnClose
        okText="Kirim"
        cancelText="Batal"
        okButtonProps={{ disabled: templateSendDisabled }}
      >
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          placeholder="Pilih template (APPROVED)"
          loading={loadingTemplates}
          value={selectedTemplate?.name}
          onChange={handleTemplateSelect}
          showSearch
          optionFilterProp="children"
        >
          {templates.map((t) => (
            <Select.Option key={t.id} value={t.name}>
              {t.name} ({t.language})
            </Select.Option>
          ))}
        </Select>

        {selectedTemplate && (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Placeholder yang didukung: <Tag>{'{{lead.full_name}}'}</Tag> <Tag>{'{{lead.first_name}}'}</Tag>
            </Text>
            {templateBodyParams.map((val, idx) => (
              <Input
                key={idx}
                placeholder={`Parameter {{${idx + 1}}}`}
                value={val}
                style={{ marginBottom: 8 }}
                onChange={(e) => {
                  const next = [...templateBodyParams];
                  next[idx] = e.target.value;
                  setTemplateBodyParams(next);
                }}
              />
            ))}
            {selectedTemplate.header_type === 'image' && (
              <Upload accept="image/*" showUploadList={false} beforeUpload={handleTemplateHeaderUpload}>
                <Button icon={<PaperClipOutlined />} loading={uploadingHeader}>
                  {templateHeaderImageUrl ? 'Ganti gambar header' : 'Unggah gambar header'}
                </Button>
              </Upload>
            )}
            {templateHeaderImageUrl && (
              <div style={{ marginTop: 8 }}>
                <Image src={templateHeaderImageUrl} width={64} height={64} style={{ objectFit: 'cover', borderRadius: 4 }} />
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default MessageThread;
